<script setup lang="ts">
/**
 * FloPay's own card fields for adding a card on the Billing page outside the first checkout (os#1615), so an
 * expiring card can be replaced once the enterprise has subscribed.
 *
 * Like the checkout form, it renders no card input of its own: the fields come from the one adapter that reaches
 * `@flopay/vue` (`lib/flopayFields.ts`), inside a container kept out of session replay. Once FloPay has saved the card it
 * emits the payment method id, the only thing about the card the portal ever holds.
 */
import { ref, shallowRef, watch } from 'vue'
import { type FloPayCardFieldsComponent, loadFloPayCardFields } from '@/lib/flopayFields'

const props = defineProps<{
    /** A FloPay field session for this enterprise's customer: FloPay's card fields are drawn once there is one. */
    fieldSession: string | null
}>()

const emit = defineEmits<{
    /** FloPay's fields saved a card: its payment method id. */
    paymentMethod: [paymentMethodId: string]
}>()

const cardFields = shallowRef<FloPayCardFieldsComponent | null>(null)
const cardFieldsLoaded = ref(false)

watch(
    () => props.fieldSession,
    async session => {
        if (session && !cardFieldsLoaded.value) {
            cardFields.value = await loadFloPayCardFields()
            cardFieldsLoaded.value = true
        }
    },
    { immediate: true },
)

function onSaved(method: { paymentMethodId?: string } | undefined): void {
    if (method?.paymentMethodId) {
        emit('paymentMethod', method.paymentMethodId)
    }
}
</script>

<template>
    <div data-testid="payment-container" class="sentry-block ph-no-capture" data-sentry-block data-rr-block>
        <component :is="cardFields" v-if="cardFields && fieldSession" :session-token="fieldSession" @saved="onSaved" />
        <p v-else class="text-sm text-muted-foreground">
            {{
                fieldSession && cardFieldsLoaded
                    ? 'Card entry is unavailable right now. Try again shortly.'
                    : "Your card is entered in FloPay's secure fields. Zero Human never sees it."
            }}
        </p>
    </div>
</template>
